import { Grid, Paper, Stack, Switch, TextField, Typography } from '@mui/material';
import { createPlanPageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { CONTRACT_TYPES, PLAN_STATUSES } from '@vy/types';
import { isNotNullOrUndefined } from '@vy/utils';
import React, { useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { VyDivider, VyTextInput } from '../../../../components/new-design/atoms';
import { VyDescriptionTitle } from '../../../../components/new-design/data-display/VyDescriptionTitle/VyDescriptionTitle';
import { VyDatePicker } from '../../../../components/new-design/date-picker';
import { SIZES } from '../../../../components/types/sizes';
import { useCreatePlanContext } from '../../../../contexts/create-plan-context';
import { PLAN_CREATION_REASONS } from '../../../../contexts/create-plan-context/create-plan-context-types/plan-create-reason';
import { CustomerAutoComplete } from '../../components/CustomerAutoComplete';
import { useGetContractPrerequisites } from '../create-plan-page-hooks/useGetContractPrerequisites';
import { PlanSettingsInputForm } from './CreatePlanForm/create-plan-form-types/plan-settings-input-form';
import { CurrencyOverrideSelect } from './CurrencyOverrideSelect';
import { PlanDurationAutoComplete } from './PlanDurationAutoComplete';
import { PlanBillingMethodRadioGroup } from './ProductBillingMethodRadioGroup/ProductBillingMethodRadioGroup';
import { TrialDurationAutoComplete } from './TrialDurationAutoComplete';

type PlanSettingsProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
export const PlanSettings: React.FC<PlanSettingsProps> = ({ className, ...props }) => {
  const {
    control,
    setValue,
    getValues,
    formState: { defaultValues },
  } = useFormContext<PlanSettingsInputForm>();

  const plan = getValues();
  const { planCreationReason, isCloningPlan } = useCreatePlanContext();
  const contractPrerequisites = useGetContractPrerequisites();

  const isActive = plan.status === PLAN_STATUSES.Active;
  const isNewContract = planCreationReason === PLAN_CREATION_REASONS.NewContract;
  const isPlanTemplate = planCreationReason === PLAN_CREATION_REASONS.NewPlan;
  const isParentAgreement =
    contractPrerequisites?.contractType === CONTRACT_TYPES.ParentAgreement ||
    plan.contractType === CONTRACT_TYPES.ParentAgreement;
  const labelPrefix = isPlanTemplate ? 'Plan' : 'Contract';
  const isRevision = planCreationReason === PLAN_CREATION_REASONS.AddPhaseToCurrentContract;
  const isCreatingContract = !isPlanTemplate && !isCloningPlan;

  const onActiveChanged = useCallback(() => {
    const newStatus = isActive ? PLAN_STATUSES.Inactive : PLAN_STATUSES.Active;
    setValue('status', newStatus);
  }, [isActive]);

  const freeTrial = getValues('trial.free');

  return (
    <section
      className={twMerge('flex h-full w-full flex-col p-6 gap-y-6 mb-6 overflow-auto', className)}
      {...props}
    >
      <Typography variant="h6">General terms</Typography>
      <Stack direction="column" sx={{
        mb: "40px"
      }}>
        <Stack
          component={Paper}
          elevation={1}
          divider={<VyDivider />}
          sx={{
            rowGap: 3,
            padding: 3
          }}>
          <Grid container size={12} sx={{
            alignItems: "center"
          }}>
            <Grid sx={{
              flex: 1
            }}>
              <VyDescriptionTitle title={`${labelPrefix} Name`} />
            </Grid>

            <Grid size={3}>
              <Controller
                control={control}
                name="name"
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...getSelectorAttribute(createPlanPageSelectors.details.nameInput)}
                    fullWidth
                    error={isNotNullOrUndefined(error)}
                    helperText={error?.message}
                    size="small"
                    {...field}
                    placeholder={`${labelPrefix} name`}
                  />
                )}
              />
            </Grid>
          </Grid>

          {isCreatingContract && (
            <Grid container size={12} sx={{
              alignItems: "center"
            }}>
              <Grid sx={{
                flex: 1
              }}>
                <VyDescriptionTitle title="Customer Name" />
              </Grid>

              <Grid size={3}>
                <CustomerAutoComplete
                  control={control}
                  name="customerId"
                  disabled={!isNewContract}
                  size={SIZES.SMALL}
                  disableClearable
                />
              </Grid>
            </Grid>
          )}

          {isCreatingContract && (
            <Grid container size={12} sx={{
              alignItems: "center"
            }}>
              <Grid sx={{
                flex: 1
              }}>
                <VyDescriptionTitle title="Start Date" />
              </Grid>

              <Grid size={3}>
                <Controller
                  control={control}
                  name="startDate"
                  render={({ field, fieldState: { error } }) => (
                    <VyDatePicker
                      fullWidth
                      size={SIZES.SMALL}
                      {...field}
                      error={isNotNullOrUndefined(error)}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          )}

          {!isParentAgreement && (
            <Grid container size={12} sx={{
              alignItems: "center"
            }}>
              <Grid sx={{
                flex: 1
              }}>
                <VyDescriptionTitle title="Plan Duration" subTitle="How long this plan stays active" />
              </Grid>

              <Grid size={3}>
                <PlanDurationAutoComplete
                  defaultValue={defaultValues?.billingData?.duration}
                  control={control}
                  name="billingData.duration"
                  currentValue={plan?.billingData?.duration}
                />
              </Grid>
            </Grid>
          )}

          {isCreatingContract && (
            <Grid container size={12} sx={{
              alignItems: "center"
            }}>
              <Grid sx={{
                flex: 1
              }}>
                <VyDescriptionTitle
                  title="Signature Date"
                  subTitle="Start date for billing and contract terms"
                />
              </Grid>

              <Grid size={3}>
                <Controller
                  control={control}
                  name="signatureDate"
                  render={({ field }) => <VyDatePicker fullWidth size={SIZES.SMALL} {...field} />}
                />
              </Grid>
            </Grid>
          )}

          {isCreatingContract && !isParentAgreement && (
            <Grid container size={12} sx={{
              alignItems: "center"
            }}>
              <Grid sx={{
                flex: 1
              }}>
                <VyDescriptionTitle
                  title="Invoices Currency"
                  subTitle="Set the invoicing currency for this contract. Exchange rate may apply"
                />
              </Grid>

              <Grid size={3}>
                <CurrencyOverrideSelect
                  control={control}
                  name="currencyOverride"
                  defaultValue={defaultValues?.currencyOverride}
                />
              </Grid>
            </Grid>
          )}

          {!isParentAgreement && (
            <Grid
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px"
              }}>
              <VyDescriptionTitle title="Select Billing Method" />
              <PlanBillingMethodRadioGroup disabled={isRevision} />
            </Grid>
          )}

          {!isParentAgreement && (
            <Grid container size={12} sx={{
              alignItems: "center"
            }}>
              <Grid sx={{
                flex: 1
              }}>
                <VyDescriptionTitle
                  title="Trial Period"
                  subTitle="Optional: set a free trial before billing begins"
                />
              </Grid>

              <Grid size={3}>
                <TrialDurationAutoComplete control={control} name="trial" freeTrial={freeTrial} label="" />
              </Grid>
            </Grid>
          )}

          {isCreatingContract && (
            <Grid container size={12} sx={{
              alignItems: "center"
            }}>
              <Grid sx={{
                flex: 1
              }}>
                <VyDescriptionTitle title="PO Number" subTitle="Add a purchase order for invoice reference" />
              </Grid>
              <Grid size={3}>
                <Controller
                  control={control}
                  name="purchaseOrder"
                  render={({ field }) => (
                    <VyTextInput fullWidth placeholder="Type PO number" size={SIZES.SMALL} {...field} />
                  )}
                />
              </Grid>
            </Grid>
          )}

          {isCreatingContract && (
            <Grid container size={12} sx={{
              alignItems: "center"
            }}>
              <Grid sx={{
                flex: 1
              }}>
                <VyDescriptionTitle
                  title="Memo"
                  subTitle="Default memo for new invoices under this contract"
                />
              </Grid>
              <Grid size={3}>
                <Controller
                  control={control}
                  name="notes"
                  render={({ field }) => (
                    <VyTextInput
                      fullWidth
                      placeholder="Type memo"
                      size={SIZES.SMALL}
                      {...field}
                      data-vy="create-plan-page-settings-notes"
                    />
                  )}
                />
              </Grid>
            </Grid>
          )}

          {!isParentAgreement && (
            <Grid container sx={{
              alignItems: "center"
            }}>
              <Grid size={6}>
                <VyDescriptionTitle
                  title="Auto Renewal"
                  subTitle="Automatically renew this plan at the end of its term"
                />
              </Grid>

              <Grid
                size={6}
                sx={{
                  justifyContent: "flex-end",
                  display: "flex"
                }}>
                <Controller
                  control={control}
                  name="billingData.autoRenewal"
                  render={({ field }) => <Switch checked={field.value} {...field} />}
                />
              </Grid>
            </Grid>
          )}

          {isCreatingContract && !isParentAgreement && (
            <Grid container sx={{
              alignItems: "center"
            }}>
              <Grid size={6}>
                <VyDescriptionTitle title="Set as Active" />
              </Grid>

              <Grid
                size={6}
                sx={{
                  justifyContent: "flex-end",
                  display: "flex"
                }}>
                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <Switch
                      checked={field.value === PLAN_STATUSES.Active}
                      {...field}
                      onChange={onActiveChanged}
                    />
                  )}
                />
              </Grid>
            </Grid>
          )}
        </Stack>
      </Stack>
    </section>
  );
};
